import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Button from './Button';
import { supabase } from '../../lib/supabase';

const NavigationBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, isAuthenticated, isAdmin, isAdvisor, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-[1000] transition-smooth ${
          isScrolled ? 'bg-card shadow-md' : 'bg-card'
        }`}
        style={{ height: '80px' }}
      >
        <div className="h-full px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-full">
            {/* Logo Section - Mobile optimized */}
            <div 
              className="flex items-center cursor-pointer touch-target"
              onClick={() => handleNavigation('/')}
            >
              <div className="navigation-logo-container">
                <div className="navigation-logo">
                  <Icon name="Mountain" size={28} color="var(--color-primary)" />
                </div>
              </div>
              <div className="ml-2 sm:ml-3">
                <h1 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-foreground font-heading">
                  Residencial 3 Volcanes
                </h1>
                <p className="text-xs text-muted-foreground font-caption hidden sm:block">
                  Millennials
                </p>
              </div>
            </div>

            {/* Desktop Navigation - Admin Menu */}
            {isAuthenticated && isAdmin && (
              <div className="hidden lg:flex items-center space-x-2 xl:space-x-3">
                <Link
                  to="/admin-dashboard"
                  className={`flex items-center space-x-2 px-3 xl:px-4 py-2 rounded-xl font-medium transition-smooth touch-target text-sm ${
                    isActivePath('/admin-dashboard')
                      ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="Shield" size={18} />
                  <span>Admin Dashboard</span>
                </Link>
                <Link
                  to="/admin-floor-plan-editor"
                  className={`flex items-center space-x-2 px-3 xl:px-4 py-2 rounded-xl font-medium transition-smooth touch-target text-sm ${
                    isActivePath('/admin-floor-plan-editor')
                      ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="FileEdit" size={18} />
                  <span className="hidden xl:inline">Gestionar Planos</span>
                  <span className="xl:hidden">Planos</span>
                </Link>
                <Link
                  to="/crm-lead-management"
                  className={`flex items-center space-x-2 px-3 xl:px-4 py-2 rounded-xl font-medium transition-smooth touch-target text-sm ${
                    isActivePath('/crm-lead-management')
                      ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="Users" size={18} />
                  <span>CRM</span>
                </Link>
                <Link
                  to="/referral-network"
                  className={`flex items-center space-x-2 px-3 xl:px-4 py-2 rounded-xl font-medium transition-smooth touch-target text-sm ${
                    isActivePath('/referral-network')
                      ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="Network" size={18} />
                  <span className="hidden xl:inline">Red de Referidos</span>
                  <span className="xl:hidden">Red</span>
                </Link>
                <Link
                  to="/project-portfolio"
                  className={`flex items-center space-x-2 px-3 xl:px-4 py-2 rounded-xl font-medium transition-smooth touch-target text-sm ${
                    isActivePath('/project-portfolio')
                      ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="Building2" size={18} />
                  <span>Proyectos</span>
                </Link>
                <Link
                  to="/interactive-floor-plans"
                  className={`flex items-center space-x-2 px-3 xl:px-4 py-2 rounded-xl font-medium transition-smooth touch-target text-sm ${
                    isActivePath('/interactive-floor-plans')
                      ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="Map" size={18} />
                  <span>Planos</span>
                </Link>
              </div>
            )}

            {/* Desktop Navigation - Advisor Menu */}
            {isAuthenticated && isAdvisor && !isAdmin && (
              <div className="hidden lg:flex items-center space-x-2 xl:space-x-3">
                <Link
                  to="/user-dashboard"
                  className={`flex items-center space-x-2 px-3 xl:px-4 py-2 rounded-xl font-medium transition-smooth touch-target text-sm ${
                    isActivePath('/user-dashboard')
                      ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="LayoutDashboard" size={18} />
                  <span>Dashboard</span>
                </Link>
                <Link
                  to="/crm-lead-management"
                  className={`flex items-center space-x-2 px-3 xl:px-4 py-2 rounded-xl font-medium transition-smooth touch-target text-sm ${
                    isActivePath('/crm-lead-management')
                      ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="Users" size={18} />
                  <span>CRM</span>
                </Link>
                <Link
                  to="/referral-network"
                  className={`flex items-center space-x-2 px-3 xl:px-4 py-2 rounded-xl font-medium transition-smooth touch-target text-sm ${
                    isActivePath('/referral-network')
                      ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="Network" size={18} />
                  <span className="hidden xl:inline">Red de Referidos</span>
                  <span className="xl:hidden">Red</span>
                </Link>
                <Link
                  to="/project-portfolio"
                  className={`flex items-center space-x-2 px-3 xl:px-4 py-2 rounded-xl font-medium transition-smooth touch-target text-sm ${
                    isActivePath('/project-portfolio')
                      ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="Building2" size={18} />
                  <span>Proyectos</span>
                </Link>
                <Link
                  to="/interactive-floor-plans"
                  className={`flex items-center space-x-2 px-3 xl:px-4 py-2 rounded-xl font-medium transition-smooth touch-target text-sm ${
                    isActivePath('/interactive-floor-plans')
                      ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="Map" size={18} />
                  <span>Planos</span>
                </Link>
              </div>
            )}
              
            {/* Desktop Navigation - Public Menu */}
            {!isAuthenticated && (
              <div className="hidden lg:flex items-center space-x-3">
                <Link
                  to="/project-portfolio"
                  className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-smooth touch-target ${
                    isActivePath('/project-portfolio')
                      ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="Building2" size={18} />
                  <span>Proyectos</span>
                </Link>
                <Link
                  to="/interactive-floor-plans"
                  className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-smooth touch-target ${
                    isActivePath('/interactive-floor-plans')
                      ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="Map" size={18} />
                  <span>Planos</span>
                </Link>
              </div>
            )}

            {/* User Profile or Auth Buttons - Desktop */}
            <div className="hidden lg:block">
              {isAuthenticated ? (
                <UserProfileDropdown userProfile={userProfile} />
              ) : (
                <div className="flex items-center space-x-2 xl:space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/signin')}
                    iconName="LogIn"
                    className="text-sm"
                  >
                    <span className="hidden xl:inline">Iniciar Sesión</span>
                    <span className="xl:hidden">Entrar</span>
                  </Button>
                  <Button
                    onClick={() => navigate('/signup')}
                    iconName="UserPlus"
                    className="text-sm"
                  >
                    Registrarse
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-muted transition-smooth touch-target focus-ring"
              aria-label="Toggle menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-[999] bg-background/95 backdrop-blur-sm lg:hidden"
          style={{ top: '80px' }}
        >
          <div className="h-full overflow-y-auto px-4 py-6">
            <div className="space-y-2">
              {/* Admin Mobile Menu */}
              {isAuthenticated && isAdmin && (
                <>
                  <button
                    onClick={() => handleNavigation('/admin-dashboard')}
                    className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl font-medium transition-smooth touch-target ${
                      isActivePath('/admin-dashboard')
                        ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="Shield" size={20} />
                    <span className="text-base">Admin Dashboard</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/admin-floor-plan-editor')}
                    className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl font-medium transition-smooth touch-target ${
                      isActivePath('/admin-floor-plan-editor')
                        ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="FileEdit" size={20} />
                    <span className="text-base">Gestionar Planos</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/crm-lead-management')}
                    className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl font-medium transition-smooth touch-target ${
                      isActivePath('/crm-lead-management')
                        ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="Users" size={20} />
                    <span className="text-base">CRM</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/referral-network')}
                    className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl font-medium transition-smooth touch-target ${
                      isActivePath('/referral-network')
                        ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="Network" size={20} />
                    <span className="text-base">Red de Referidos</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/project-portfolio')}
                    className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl font-medium transition-smooth touch-target ${
                      isActivePath('/project-portfolio')
                        ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="Building2" size={20} />
                    <span className="text-base">Proyectos</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/interactive-floor-plans')}
                    className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl font-medium transition-smooth touch-target ${
                      isActivePath('/interactive-floor-plans')
                        ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="Map" size={20} />
                    <span className="text-base">Planos</span>
                  </button>
                </>
              )}

              {/* Advisor Mobile Menu */}
              {isAuthenticated && isAdvisor && !isAdmin && (
                <>
                  <button
                    onClick={() => handleNavigation('/user-dashboard')}
                    className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl font-medium transition-smooth touch-target ${
                      isActivePath('/user-dashboard')
                        ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="LayoutDashboard" size={20} />
                    <span className="text-base">Dashboard</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/crm-lead-management')}
                    className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl font-medium transition-smooth touch-target ${
                      isActivePath('/crm-lead-management')
                        ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="Users" size={20} />
                    <span className="text-base">CRM</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/referral-network')}
                    className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl font-medium transition-smooth touch-target ${
                      isActivePath('/referral-network')
                        ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="Network" size={20} />
                    <span className="text-base">Red de Referidos</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/project-portfolio')}
                    className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl font-medium transition-smooth touch-target ${
                      isActivePath('/project-portfolio')
                        ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="Building2" size={20} />
                    <span className="text-base">Proyectos</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/interactive-floor-plans')}
                    className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl font-medium transition-smooth touch-target ${
                      isActivePath('/interactive-floor-plans')
                        ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="Map" size={20} />
                    <span className="text-base">Planos</span>
                  </button>
                </>
              )}

              {/* Public Mobile Menu */}
              {!isAuthenticated && (
                <>
                  <button
                    onClick={() => handleNavigation('/project-portfolio')}
                    className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl font-medium transition-smooth touch-target ${
                      isActivePath('/project-portfolio')
                        ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="Building2" size={20} />
                    <span className="text-base">Proyectos</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/interactive-floor-plans')}
                    className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl font-medium transition-smooth touch-target ${
                      isActivePath('/interactive-floor-plans')
                        ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="Map" size={20} />
                    <span className="text-base">Planos</span>
                  </button>
                </>
              )}
              
              <div className="pt-4 border-t border-border mt-4">
                {isAuthenticated ? (
                  <>
                    <button
                      onClick={async () => {
                        await signOut();
                        setIsMobileMenuOpen(false);
                        navigate('/');
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-4 rounded-xl font-medium text-error hover:bg-error/10 transition-smooth touch-target"
                    >
                      <Icon name="LogOut" size={20} />
                      <span className="text-base">Cerrar Sesión</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleNavigation('/signin')}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-4 rounded-xl font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-smooth touch-target mb-2"
                    >
                      <Icon name="LogIn" size={20} />
                      <span className="text-base">Iniciar Sesión</span>
                    </button>
                    <button
                      onClick={() => handleNavigation('/signup')}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-4 rounded-xl font-medium border border-primary text-primary hover:bg-primary/10 transition-smooth touch-target"
                    >
                      <Icon name="UserPlus" size={20} />
                      <span className="text-base">Registrarse</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const UserProfileDropdown = ({ userProfile }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { signOut, isAdmin } = useAuth();

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
    setIsOpen(false);
  };

  const handleDashboard = () => {
    if (isAdmin) {
      navigate('/admin-dashboard');
    } else {
      navigate('/user-dashboard');
    }
    setIsOpen(false);
  };

  const handleReferralNetwork = () => {
    navigate('/referral-network');
    setIsOpen(false);
  };

  const handleCRM = () => {
    navigate('/crm-lead-management');
    setIsOpen(false);
  };

  const handleAdminFloorPlans = () => {
    navigate('/admin-floor-plan-editor');
    setIsOpen(false);
  };

  const handleReferralLink = async () => {
    try {
      const { data } = await supabase?.from('referral_links')?.select('referral_code')?.eq('user_id', userProfile?.id)?.single();
      
      const referralLink = `${window.location?.origin}/landing-page?ref=${data?.referral_code || 'USER123'}`;
      await navigator.clipboard?.writeText(referralLink);
      setIsOpen(false);
    } catch (error) {
      console.error('Error copying referral link:', error);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name?.split(' ');
    if (parts?.length >= 2) {
      return `${parts?.[0]?.charAt(0)}${parts?.[1]?.charAt(0)}`?.toUpperCase();
    }
    return name?.charAt(0)?.toUpperCase();
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 p-2 rounded-xl hover:bg-muted transition-smooth focus-ring"
      >
        <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
          <span className="text-primary-foreground font-medium">
            {getInitials(userProfile?.full_name)}
          </span>
        </div>
        <Icon 
          name={isOpen ? 'ChevronUp' : 'ChevronDown'} 
          size={20} 
          color="var(--color-foreground)"
        />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-[1040]" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-64 bg-popover rounded-xl shadow-lg z-[1050] overflow-hidden">
            <div className="p-4 border-b border-border">
              <p className="font-medium text-foreground">{userProfile?.full_name}</p>
              <p className="text-sm text-muted-foreground">{userProfile?.email}</p>
              <p className="text-xs text-primary font-medium mt-1">
                {isAdmin ? 'Administrador' : 'Asesor'}
              </p>
            </div>
            <div className="py-2">
              <button
                onClick={handleDashboard}
                className="w-full flex items-center px-4 py-3 hover:bg-muted transition-smooth text-left"
              >
                <Icon name={isAdmin ? 'Shield' : 'LayoutDashboard'} size={20} className="mr-3" />
                <span>{isAdmin ? 'Admin Dashboard' : 'Mi Dashboard'}</span>
              </button>
              {isAdmin && (
                <button
                  onClick={handleAdminFloorPlans}
                  className="w-full flex items-center px-4 py-3 hover:bg-muted transition-smooth text-left"
                >
                  <Icon name="FileEdit" size={20} className="mr-3" />
                  <span>Gestionar Planos</span>
                </button>
              )}
              <button
                onClick={handleReferralNetwork}
                className="w-full flex items-center px-4 py-3 hover:bg-muted transition-smooth text-left"
              >
                <Icon name="Network" size={20} className="mr-3" />
                <span>Red de Referidos</span>
              </button>
              <button
                onClick={handleCRM}
                className="w-full flex items-center px-4 py-3 hover:bg-muted transition-smooth text-left"
              >
                <Icon name="Users" size={20} className="mr-3" />
                <span>CRM</span>
              </button>
              <button
                onClick={handleReferralLink}
                className="w-full flex items-center px-4 py-3 hover:bg-muted transition-smooth text-left"
              >
                <Icon name="Link" size={20} className="mr-3" />
                <span>Copiar Link de Referido</span>
              </button>
              <button
                onClick={handleSignOut}
                className="w-full flex items-center px-4 py-3 hover:bg-muted transition-smooth text-left text-error"
              >
                <Icon name="LogOut" size={20} className="mr-3" />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NavigationBar;